import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Store, ShoppingBag, ClipboardList, Package, Tag, Boxes, CreditCard, Percent, ShieldCheck, Bell, AlertTriangle, BarChart3, Shield, History, Settings, Megaphone } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import AdminOverview from "@/components/AdminOverview";
import AdminBanners from "@/components/AdminBanners";
import AdminMerchants from "@/components/admin/AdminMerchants";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminOrdersPage from "@/components/admin/AdminOrdersPage";
import AdminProductsPage from "@/components/admin/AdminProductsPage";
import AdminCategories from "@/components/admin/AdminCategories";
import AdminInventory from "@/components/admin/AdminInventory";
import AdminPaymentsPage from "@/components/admin/AdminPaymentsPage";
import AdminCommissions from "@/components/admin/AdminCommissions";
import AdminVerification from "@/components/admin/AdminVerification";
import AdminNotifications from "@/components/admin/AdminNotifications";
import AdminDisputes from "@/components/admin/AdminDisputes";
import AdminReports from "@/components/admin/AdminReports";
import AdminPermissions from "@/components/admin/AdminPermissions";
import AdminActivityPage from "@/components/admin/AdminActivityPage";
import AdminSettingsPage from "@/components/admin/AdminSettingsPage";

export default function Admin() {
  const nav = useNavigate();
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState("overview");

  useEffect(() => {
    if (!ready) return;
    if (user === false) { nav("/auth?next=/admin"); return; }
    if (user && user.role !== "admin") { toast.error("صلاحيات المدير مطلوبة"); nav("/"); return; }
    client.get("/admin/stats").then((r) => setStats(r.data)).catch(() => { toast.error("صلاحيات المدير مطلوبة"); nav("/"); });
  }, [ready, user]);

  // AdminOverview action cards use keys: verification, orders, products, payments, disputes
  const goto = (k) => {
    const map = { verification: "verification", products: "products" };
    setActive(map[k] || k);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navItems = [
    { key: "overview", label: "الرئيسية", icon: LayoutDashboard },
    { key: "banners", label: "الإعلانات", icon: Megaphone },
    { key: "merchants", label: "التجار", icon: Store, badge: stats?.pending || 0 },
    { key: "buyers", label: "المشترون", icon: ShoppingBag },
    { key: "orders", label: "الطلبات", icon: ClipboardList },
    { key: "products", label: "المنتجات", icon: Package },
    { key: "categories", label: "الأقسام", icon: Tag },
    { key: "inventory", label: "المخزون", icon: Boxes },
    { key: "payments", label: "المدفوعات", icon: CreditCard },
    { key: "commissions", label: "العمولات", icon: Percent },
    { key: "verification", label: "التوثيق", icon: ShieldCheck, badge: stats?.pending || 0 },
    { key: "notifications", label: "الإشعارات", icon: Bell },
    { key: "disputes", label: "النزاعات والاستردادات", icon: AlertTriangle },
    { key: "reports", label: "التقارير", icon: BarChart3 },
    { key: "permissions", label: "الصلاحيات", icon: Shield },
    { key: "activity", label: "سجل النشاط", icon: History },
    { key: "settings", label: "الإعدادات", icon: Settings },
  ];

  const PAGES = {
    overview: <AdminOverview onSelect={goto} />,
    banners: <AdminBanners />,
    merchants: <AdminMerchants />,
    buyers: <AdminUsers role="buyer" title="حسابات المشترين" icon={ShoppingBag} />,
    orders: <AdminOrdersPage />,
    products: <AdminProductsPage />,
    categories: <AdminCategories />,
    inventory: <AdminInventory />,
    payments: <AdminPaymentsPage />,
    commissions: <AdminCommissions />,
    verification: <AdminVerification />,
    notifications: <AdminNotifications />,
    disputes: <AdminDisputes />,
    reports: <AdminReports />,
    permissions: <AdminPermissions />,
    activity: <AdminActivityPage />,
    settings: <AdminSettingsPage />,
  };
  const label = navItems.find((n) => n.key === active)?.label || "لوحة الإدارة";

  return (
    <DashboardShell roleLabel="مدير النظام" topLabel={label} items={navItems} active={active} onSelect={goto}>
      <div key={active} className="k-rise">{PAGES[active]}</div>
    </DashboardShell>
  );
}
